'use client'

import { CustomFlowbiteTheme, Datepicker } from 'flowbite-react'
import CompactDatepicker from '@/lib/react-tailwindcss-datepicker'
import { DateValueType } from '@/lib/react-tailwindcss-datepicker/dist/types'
import TimePicker from 'react-time-picker'
import '/assets/styles/TimePicker.css'
import 'react-clock/dist/Clock.css'
import { fromZonedTime, formatInTimeZone } from 'date-fns-tz'
import { FocusEventHandler, useEffect, useState } from 'react'
import { format, parse } from 'date-fns'

const customDatePickerTheme: CustomFlowbiteTheme['datepicker'] = {
  views: {
    days: {
      items: {
        item: {
          disabled: 'text-gray-300',
        },
      },
    },
    months: {
      items: {
        item: {
          disabled: 'text-gray-300',
        },
      },
    },
    years: {
      items: {
        item: {
          disabled: 'text-gray-300',
        },
      },
    },
  },
}

const DATE_FORMAT = 'MMM dd, yyyy'
const ISO_DATE_FORMAT = 'yyyy-MM-dd'
const TIME_FORMAT = 'HH:mm'

export interface DateTimePickerProps {
  timezone: string
  value?: Date | null
  onChange: (value: Date | undefined) => void
  onBlur?: FocusEventHandler<HTMLInputElement>
  color?: string
  helperText?: string
  defaultTime?: string
  minDate?: Date
  maxDate?: Date
  timeLayout?: string
  compactDate?: boolean
}

const DateTimePicker = ({
  timezone,
  value,
  onChange,
  onBlur,
  color,
  helperText,
  defaultTime,
  minDate,
  maxDate,
  timeLayout,
  compactDate,
}: DateTimePickerProps) => {
  const safeTimezone = timezone && timezone.trim() ? timezone : 'UTC'
  const internalDateFormat = compactDate ? ISO_DATE_FORMAT : DATE_FORMAT

  function formatWithTimezone(date: Date, fmt: string) {
    try {
      return formatInTimeZone(date, safeTimezone, fmt)
    } catch {
      return formatInTimeZone(date, 'UTC', fmt)
    }
  }

  function handleDateChange(dateValue: Date) {
    const newDate = format(dateValue, internalDateFormat)
    setDate(newDate)
    dispatchOnChange(newDate, time)
  }

  function handleCompactDateChange(picked: DateValueType) {
    const startDate = picked?.startDate
    if (!startDate) {
      setDate('')
      dispatchOnChange('', time)
      return
    }
    const dateObj =
      startDate instanceof Date ? startDate : new Date(startDate as string)
    handleDateChange(dateObj)
  }

  function handleTimeChange(timeValue: string | null) {
    setTime(timeValue)
    dispatchOnChange(date, timeValue)
  }

  function dispatchOnChange(date?: string | null, time?: string | null) {
    if (!date || !time) {
      onChange(undefined)
      return
    }

    try {
      onChange(
        fromZonedTime(
          parse(
            `${date} ${time}`,
            `${internalDateFormat} ${TIME_FORMAT}`,
            new Date(),
          ),
          safeTimezone,
        ),
      )
    } catch {
      onChange(undefined)
    }
  }

  const [date, setDate] = useState(
    value ? formatWithTimezone(value, internalDateFormat) : '',
  )
  const [time, setTime] = useState<string | undefined | null>(
    value ? formatWithTimezone(value, TIME_FORMAT) : defaultTime,
  )

  useEffect(() => {
    setDate(value ? formatWithTimezone(value, internalDateFormat) : '')
    setTime(value ? formatWithTimezone(value, TIME_FORMAT) : defaultTime)
  }, [value, defaultTime, timezone, internalDateFormat])

  return (
    <div>
      <div className="flex gap-3">
        {compactDate ? (
          <div className="grow">
            <CompactDatepicker
              asSingle
              useRange={false}
              displayFormat="MMM DD"
              value={
                date
                  ? { startDate: new Date(date), endDate: new Date(date) }
                  : { startDate: null, endDate: null }
              }
              onChange={handleCompactDateChange}
              minDate={minDate ?? null}
              maxDate={maxDate ?? null}
              placeholder="Select date"
              inputClassName={`block w-full text-sm rounded-lg border p-2.5 ${
                color === 'failure'
                  ? 'bg-red-50 text-red-900 border-red-500'
                  : 'border-gray-300 bg-white text-gray-900'
              }`}
              toggleClassName="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
            />
          </div>
        ) : (
          <Datepicker
            // flowbite's Datepicker only treats `null` as "no value" (shows the
            // placeholder); `undefined` falls through to displaying new Date()
            // internally while leaving its own selectedDate at null, so it
            // silently shows "today" without ever calling onChange.
            value={(date ? new Date(date) : null) as never}
            onChange={((d: Date) => d && handleDateChange(d)) as never}
            onBlur={onBlur}
            color={color}
            showClearButton={false}
            showTodayButton={false}
            minDate={minDate}
            maxDate={maxDate}
            className="grow"
            placeholder="Select date"
            theme={customDatePickerTheme}
          ></Datepicker>
        )}
        <TimePicker
          onChange={handleTimeChange}
          value={time}
          format={TIME_FORMAT}
          clearIcon={false}
          clockIcon={compactDate ? null : undefined}
          disableClock={compactDate}
          className={`${
            timeLayout || (compactDate ? 'w-28 shrink-0' : 'basis-80')
          } ${color === 'failure' ? 'bg-red-50 text-red-900 *:!border-red-500' : ''}`}
          onBlur={onBlur}
        ></TimePicker>
      </div>
      {helperText && <p className="mt-2 text-sm text-red-600">{helperText}</p>}
    </div>
  )
}

export default DateTimePicker
